/**
 * stripGps.js — remove the GPS EXIF block from a JPEG blob.
 *
 * FT-342 (2026-07-08) — capture pipeline.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §4.2 (stage 2).
 *
 * Uses piexifjs to load EXIF, delete the GPSIFD block, and re-insert. Other
 * EXIF (orientation, camera model, DateTimeOriginal shutter-time) is preserved
 * so subsequent decode stages still see orientation metadata and MCA/incident
 * evidence retains the shutter timestamp.
 *
 * FT-342 pre-merge fix: switched from `piexif.remove` (drops ALL EXIF) to a
 * surgical `delete exifObj.GPS` + dump+insert flow. DateTimeOriginal survives
 * this pass so `usePhotoCapture` can extract it as the record `capturedAt`.
 *
 * piexifjs only handles JPEG. If the input is not JPEG, we return the blob
 * unchanged (safe pass-through — the canvas re-encode will drop any EXIF).
 *
 * FT-349 (2026-07-09) — GPS-per-recordType policy note.
 * This helper is NO LONGER invoked unconditionally by the capture pipeline.
 * GPS is PRESERVED for `equipment_document`, `maintenance_job`, `defect`
 * (subject = a thing at a known location — GPS corroborates MCA evidence).
 * GPS is STRIPPED only for `crew_profile` (subject = a person — GPS is a
 * privacy leak). Deliberate policy — locked by advisor 2026-07-09 (FT-349).
 * See the call-site in hooks/usePhotoCapture.js runPipeline() for the gate.
 */

const JPEG_MIME_RE = /^image\/jpe?g$/i;

/**
 * Strip the GPS EXIF block (only) from a JPEG blob. Non-JPEG input is passed
 * through unchanged. Non-GPS EXIF (including DateTimeOriginal) is preserved.
 * @param {Blob} blob
 * @returns {Promise<Blob>}
 */
export async function stripGpsAndExif(blob) {
  if (!isBlobLike(blob)) {
    throw new Error('[stripGps] expected Blob input');
  }
  // Non-JPEG: piexifjs will throw. Pass through — canvas re-encode drops EXIF anyway.
  if (!JPEG_MIME_RE.test(blob.type)) return blob;

  // piexifjs is a CommonJS module. Static import is fine — it's ~30 KB.
  const piexifMod = await import('piexifjs');
  const piexif = piexifMod.default || piexifMod;

  const dataUrl = await blobToDataUrl(blob);
  let stripped;
  try {
    // Surgical strip: load EXIF, delete only the GPS IFD, re-dump, re-insert.
    // This preserves DateTimeOriginal (shutter-time) and orientation metadata.
    const exifObj = piexif.load(dataUrl);
    if (exifObj && exifObj.GPS) {
      delete exifObj.GPS;
    }
    const exifBytes = piexif.dump(exifObj || {});
    stripped = piexif.insert(exifBytes, dataUrl);
  } catch (err) {
    // piexifjs can throw on malformed JPEG. Fall back to pass-through — canvas
    // re-encode downstream will still drop EXIF.
    return blob;
  }
  return dataUrlToBlob(stripped);
}

function isBlobLike(v) {
  return v && typeof v === 'object' && typeof v.size === 'number' && typeof v.type === 'string' && typeof v.slice === 'function';
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function dataUrlToBlob(dataUrl) {
  const [meta, base64] = dataUrl.split(',');
  const mimeMatch = /data:([^;]+);base64/.exec(meta);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

export default stripGpsAndExif;
